import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, timer, interval } from 'rxjs';
import { map, filter, take, switchMap } from 'rxjs/operators';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  persistent?: boolean;
  actions?: NotificationAction[];
  timestamp: Date;
}

export interface NotificationAction {
  label: string;
  action: () => void;
  style?: 'primary' | 'secondary' | 'danger';
}

export interface NotificationConfig {
  maxNotifications: number;
  defaultDuration: number;
  position: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  animation: 'slide' | 'fade' | 'bounce';
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private notifications = new BehaviorSubject<Notification[]>([]);
  private config: NotificationConfig = {
    maxNotifications: 5,
    defaultDuration: 5000,
    position: 'top-right',
    animation: 'slide'
  };

  constructor() {
    // Limpar notificações expiradas a cada 30 segundos
    interval(30000).subscribe(() => {
      this.cleanExpiredNotifications();
    });
  }

  /**
   * Configura o serviço de notificações
   */
  configure(config: Partial<NotificationConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Obtém o observable de notificações
   */
  getNotifications(): Observable<Notification[]> {
    return this.notifications.asObservable();
  }

  /**
   * Adiciona uma nova notificação
   */
  show(notification: Omit<Notification, 'id' | 'timestamp'>): string {
    const id = this.generateId();
    const newNotification: Notification = {
      ...notification,
      id,
      timestamp: new Date(),
      duration: notification.duration || this.config.defaultDuration,
      persistent: notification.persistent || false
    };

    const currentNotifications = this.notifications.value;
    const updatedNotifications = [...currentNotifications, newNotification];

    // Limitar número máximo de notificações
    if (updatedNotifications.length > this.config.maxNotifications) {
      updatedNotifications.splice(0, updatedNotifications.length - this.config.maxNotifications);
    }

    this.notifications.next(updatedNotifications);

    // Auto-remover se não for persistente
    if (!newNotification.persistent && newNotification.duration > 0) {
      timer(newNotification.duration).subscribe(() => {
        this.remove(newNotification.id);
      });
    }

    return id;
  }

  /**
   * Adiciona notificação de sucesso
   */
  success(title: string, message: string, options?: Partial<Notification>): string {
    return this.show({
      title,
      message,
      type: 'success',
      ...options
    });
  }

  /**
   * Adiciona notificação de erro
   */
  error(title: string, message: string, options?: Partial<Notification>): string {
    return this.show({
      title,
      message,
      type: 'error',
      persistent: true,
      ...options
    });
  }

  /**
   * Adiciona notificação de aviso
   */
  warning(title: string, message: string, options?: Partial<Notification>): string {
    return this.show({
      title,
      message,
      type: 'warning',
      ...options
    });
  }

  /**
   * Adiciona notificação de informação
   */
  info(title: string, message: string, options?: Partial<Notification>): string {
    return this.show({
      title,
      message,
      type: 'info',
      ...options
    });
  }

  /**
   * Remove uma notificação específica
   */
  remove(id: string): void {
    const currentNotifications = this.notifications.value;
    const updatedNotifications = currentNotifications.filter(n => n.id !== id);
    this.notifications.next(updatedNotifications);
  }

  /**
   * Remove todas as notificações
   */
  clear(): void {
    this.notifications.next([]);
  }

  /**
   * Remove notificações por tipo
   */
  clearByType(type: Notification['type']): void {
    const currentNotifications = this.notifications.value;
    const updatedNotifications = currentNotifications.filter(n => n.type !== type);
    this.notifications.next(updatedNotifications);
  }

  /**
   * Obtém notificações por tipo
   */
  getByType(type: Notification['type']): Observable<Notification[]> {
    return this.notifications.pipe(
      map(notifications => notifications.filter(n => n.type === type))
    );
  }

  /**
   * Obtém notificações não lidas
   */
  getUnread(): Observable<Notification[]> {
    return this.notifications.pipe(
      map(notifications => notifications.filter(n => !n.persistent))
    );
  }

  /**
   * Obtém notificações persistentes
   */
  getPersistent(): Observable<Notification[]> {
    return this.notifications.pipe(
      map(notifications => notifications.filter(n => n.persistent))
    );
  }

  /**
   * Obtém estatísticas das notificações
   */
  getStats(): Observable<{
    total: number;
    byType: Record<Notification['type'], number>;
    unread: number;
    persistent: number;
  }> {
    return this.notifications.pipe(
      map(notifications => {
        const stats = {
          total: notifications.length,
          byType: {
            success: 0,
            error: 0,
            warning: 0,
            info: 0
          },
          unread: 0,
          persistent: 0
        };

        notifications.forEach(notification => {
          stats.byType[notification.type]++;
          if (!notification.persistent) stats.unread++;
          if (notification.persistent) stats.persistent++;
        });

        return stats;
      })
    );
  }

  /**
   * Cria uma notificação de progresso
   */
  createProgressNotification(
    title: string,
    progress$: Observable<number>,
    onComplete?: () => void
  ): string {
    const notificationId = this.show({
      title,
      message: '0%',
      type: 'info',
      persistent: true
    });

    progress$.subscribe(progress => {
      const message = `${Math.round(progress)}%`;
      this.updateMessage(notificationId, message);
      
      if (progress >= 100) {
        this.remove(notificationId);
        if (onComplete) onComplete();
      }
    });

    return notificationId;
  }

  /**
   * Atualiza a mensagem de uma notificação
   */
  updateMessage(id: string, message: string): void {
    const currentNotifications = this.notifications.value;
    const updatedNotifications = currentNotifications.map(n => 
      n.id === id ? { ...n, message } : n
    );
    this.notifications.next(updatedNotifications);
  }

  /**
   * Cria uma notificação de confirmação
   */
  confirm(
    title: string,
    message: string,
    onConfirm: () => void,
    onCancel?: () => void
  ): string {
    return this.show({
      title,
      message,
      type: 'warning',
      persistent: true,
      actions: [
        {
          label: 'Confirmar',
          action: () => {
            onConfirm();
            this.remove(this.generateId());
          },
          style: 'primary'
        },
        {
          label: 'Cancelar',
          action: () => {
            if (onCancel) onCancel();
            this.remove(this.generateId());
          },
          style: 'secondary'
        }
      ]
    });
  }

  /**
   * Cria uma notificação de loading
   */
  loading(title: string, message: string = 'Carregando...'): string {
    return this.show({
      title,
      message,
      type: 'info',
      persistent: true
    });
  }

  /**
   * Remove notificações expiradas
   */
  private cleanExpiredNotifications(): void {
    const currentNotifications = this.notifications.value;
    const now = new Date();
    
    const validNotifications = currentNotifications.filter(notification => {
      if (notification.persistent) return true;
      
      const elapsed = now.getTime() - notification.timestamp.getTime();
      return elapsed < (notification.duration || this.config.defaultDuration);
    });

    if (validNotifications.length !== currentNotifications.length) {
      this.notifications.next(validNotifications);
    }
  }

  /**
   * Gera ID único para notificação
   */
  private generateId(): string {
    return `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Obtém configuração atual
   */
  getConfig(): NotificationConfig {
    return { ...this.config };
  }

  /**
   * Cria uma notificação de sistema
   */
  system(message: string, type: Notification['type'] = 'info'): string {
    return this.show({
      title: 'Sistema',
      message,
      type,
      persistent: false
    });
  }

  /**
   * Cria uma notificação de atualização
   */
  update(message: string, version?: string): string {
    return this.show({
      title: version ? `Atualização ${version}` : 'Atualização',
      message,
      type: 'info',
      persistent: false
    });
  }
}
